import { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import 'react-multi-date-picker/styles/layouts/mobile.css'
import { Grid, IconButton, Tooltip } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'

import Button from '../components/Button'
import Dialog from '../components/Dialog'
import Select from '../components/Select'
import GridHeader from '../components/GridHeader'
import InputField from '../components/InputField'
import DatesPicker from '../components/DatesPicker'
import AutoComplete from '../components/AutoComplete'
import GridCheckbox from '../components/GridCheckbox'
import GridWeeklyTimeSlot from '../components/GridWeeklyTimeSlot'
import GridWeeklyTimeSlotChoice from '../components/GridWeeklyTimeSlotChoice'
import { config } from '../settings/configs'
import {
  colours,
  titlesArbitro as titles,
  phrasesArbitro as phrases,
  TITOLI_STUDIO,
} from '../settings/settings'
import { useFeedback } from '../hooks/feedback-hook'
import {
  useGetAffiliazioniLightQuery,
  useGetArbitroByIdQuery,
  useCreateArbitroMutation,
  useUpdateArbitroMutation,
} from '../hooks/api-slice'
import {
  fromDateToString,
  fromStringToDate,
  formatIndirizzo,
  linkToGoogleMaps,
  formatBodyField,
  useSeasonRange,
} from '../hooks/utils-functions'
import { setArbitro, setArbitroField } from '../container/arbitri'

const Arbitro = () => {
  const infoUtente = useSelector(state => state.home.infoUtente)
  const { admin, arbitro, designatore, idArbitro } = infoUtente
  const { data: affiliazioniLight } = useGetAffiliazioniLightQuery(undefined, {
    skip: !(admin || designatore),
  })
  const isMobile = useSelector(state => state.home.isMobile)
  const Arbitro = useSelector(state => state.arbitri.arbitro)

  const dispatch = useDispatch()

  const { data: arbitroData } = useGetArbitroByIdQuery(idArbitro, {
    skip: !(arbitro && idArbitro),
  })

  useEffect(() => {
    if (arbitroData) dispatch(setArbitro(arbitroData))
  }, [arbitroData, dispatch])

  const [createArbitroMutation, { isLoading: isCreating }] =
    useCreateArbitroMutation()
  const [updateArbitroMutation, { isLoading: isUpdating }] =
    useUpdateArbitroMutation()

  const aDispatch = useCallback(
    (field, value, toCase) =>
      dispatch(
        setArbitroField({ field, value: formatBodyField(value, toCase) }),
      ),
    [dispatch],
  )

  const mode = !!Arbitro?._id ? 'update' : 'add'

  const { setFeedback } = useFeedback()

  const docTitle = `${mode === 'add' ? 'Nuovo' : 'Modifica'} arbitro`
  document.title = `${config?.name} - ${docTitle}`

  const [domicilioResidenza, setDomicilioResidenza] = useState()
  const { minDate, maxDate } = useSeasonRange()

  // filter indisponibilitaPuntuali inside/outside current season range
  const fIndPun = interval =>
    Arbitro?.indisponibilitaPuntuali
      ?.map(i => fromStringToDate(i))
      ?.filter(i => {
        if (!minDate || !maxDate) return interval === 'in'
        if (interval === 'in') return i >= minDate && i <= maxDate
        if (interval === 'out') return i < minDate || i > maxDate
        return true
      }) || []

  const setIndisponibilitaPuntuali = i =>
    aDispatch(
      'indisponibilitaPuntuali',
      [...fIndPun('out'), ...(i || [])]
        ?.sort((a, b) => a - b)
        ?.map(x => fromDateToString(new Date(x))) || [],
    )

  const [openDisponibilita, setOpenDisponibilita] = useState()
  const [openSocieta, setOpenSocieta] = useState()
  const [openIndisp, setOpenIndisp] = useState()
  const [openIndispTyping, setOpenIndispTyping] = useState()
  const [openActive, setOpenActive] = useState()

  const handleClickOpenDisponibilita = () => setOpenDisponibilita(true)
  const handleClickOpenSocieta = () => setOpenSocieta(true)
  const handleClickOpenIndisp = () => setOpenIndisp(true)
  const handleClickOpenIndispTyping = () => setOpenIndispTyping(true)
  const handleClickOpenActive = () => setOpenActive(true)

  const handleCloseDisponibilita = () => setOpenDisponibilita()
  const handleCloseSocieta = () => setOpenSocieta()
  const handleCloseIndisp = () => setOpenIndisp()
  const handleCloseIndispTyping = () => setOpenIndispTyping()
  const handleCloseActive = () => setOpenActive()

  // update domicilioResidenza
  useEffect(() => {
    if (typeof domicilioResidenza !== 'boolean')
      setDomicilioResidenza(
        Arbitro?._id &&
          Arbitro?.indirizzoDomicilio === Arbitro?.indirizzoResidenza &&
          Arbitro?.cittaDomicilio === Arbitro?.cittaResidenza &&
          Arbitro?.capDomicilio === Arbitro?.capResidenza,
      )
  }, [
    Arbitro?._id,
    Arbitro?.capDomicilio,
    Arbitro?.capResidenza,
    Arbitro?.cittaDomicilio,
    Arbitro?.cittaResidenza,
    Arbitro?.indirizzoDomicilio,
    Arbitro?.indirizzoResidenza,
    domicilioResidenza,
  ])

  // update indirizzoDomicilio
  useEffect(() => {
    if (
      domicilioResidenza &&
      (Arbitro?.indirizzoDomicilio !== Arbitro?.indirizzoResidenza ||
        Arbitro?.cittaDomicilio !== Arbitro?.cittaResidenza ||
        Arbitro?.capDomicilio !== Arbitro?.capResidenza)
    ) {
      aDispatch('indirizzoDomicilio', Arbitro?.indirizzoResidenza)
      aDispatch('cittaDomicilio', Arbitro?.cittaResidenza)
      aDispatch('capDomicilio', Arbitro?.capResidenza)
    }
  }, [
    aDispatch,
    Arbitro?.capDomicilio,
    Arbitro?.capResidenza,
    Arbitro?.cittaDomicilio,
    Arbitro?.cittaResidenza,
    Arbitro?.indirizzoDomicilio,
    Arbitro?.indirizzoResidenza,
    domicilioResidenza,
  ])

  const addArbitro = async () => {
    const result = await createArbitroMutation(Arbitro)
    if (!result?.error)
      setFeedback(titles.titleSuccessAdd, false, '', '/arbitri')
  }

  const updateArbitro = async () => {
    const arg = { id: Arbitro?._id, ...Arbitro }
    const result = await updateArbitroMutation(arg)
    if (!result?.error)
      setFeedback(titles.titleSuccessUpdate, false, '', '/arbitri')
  }

  const confirmArbitro = async () => {
    if (mode === 'add') await addArbitro()
    if (mode === 'update') await updateArbitro()
  }

  // trigger functions onComponentUnmounts
  useEffect(() => {
    return () => dispatch(setArbitro())
  }, [dispatch])

  const containerProps = {
    container: true,
    paddingY: 2,
    rowSpacing: 2,
    columnSpacing: 5,
    sx: { width: '100%' },
  }

  return (
    <Grid container paddingX={2} sx={{ width: '100%' }}>
      <GridHeader h2 text={docTitle} />
      <Grid {...containerProps}>
        <Grid size={{ xs: 12, md: 6 }}>
          <InputField
            id='nome'
            value={Arbitro?.nome}
            onChange={i => aDispatch('nome', i, 'upper')}
            autoFocus
            label='Nome *'
            errorFunc={() => !Arbitro?.nome}
            mode={mode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <InputField
            id='cognome'
            value={Arbitro?.cognome}
            onChange={i => aDispatch('cognome', i, 'upper')}
            label='Cognome *'
            errorFunc={() => !Arbitro?.cognome}
            mode={mode}
          />
        </Grid>
        {admin && (
          <Grid size={{ xs: 12, md: 6 }}>
            <InputField
              id='codice-fiscale'
              value={Arbitro?.codiceFiscale}
              onChange={i =>
                aDispatch('codiceFiscale', i.replace(/\W/g, ''), 'upper')
              }
              label='Codice fiscale *'
              errorFunc={() => !Arbitro?.codiceFiscale}
              mode={mode}
              readOnly={!admin}
            />
          </Grid>
        )}
        {admin && mode === 'add' && (
          <Grid size={{ xs: 12, md: 6 }}>
            <InputField
              id='password'
              value={Arbitro?.password}
              onChange={i => aDispatch('password', i)}
              label='Password *'
              errorFunc={() => !Arbitro?.password}
              mode={mode}
              readOnly={!admin}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, md: 6 }}>
          <InputField
            id='data-nascita'
            value={Arbitro?.dataNascita}
            onChange={i => aDispatch('dataNascita', i)}
            label='Data di nascita'
            errorFunc={() => !Arbitro?.dataNascita}
            adornment='date'
            mode={mode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <InputField
            id='luogo-nascita'
            value={Arbitro?.luogoNascita}
            onChange={i => aDispatch('luogoNascita', i, 'upper')}
            label='Luogo di nascita'
            errorFunc={() => !Arbitro?.luogoNascita}
            adornment='city'
            mode={mode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <InputField
            id='tessera'
            value={Arbitro?.tesseraPGS}
            onChange={i => aDispatch('tesseraPGS', i)}
            label='Tessera PGS'
            errorFunc={() => !Arbitro?.tesseraPGS}
            mode={mode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Select
            id='titolo-studio'
            value={Arbitro?.titoloStudio}
            options={TITOLI_STUDIO}
            label='Titolo di studio'
            onChange={i => aDispatch('titoloStudio', i)}
            mode={mode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <InputField
            id='scadenza-visita-medica'
            value={Arbitro?.scadenzaVisitaMedica}
            onChange={i => aDispatch('scadenzaVisitaMedica', i)}
            label='Scadenza visita medica'
            errorFunc={() =>
              fromStringToDate(Arbitro?.scadenzaVisitaMedica) < new Date()
            }
            adornment='date'
            mode={mode}
          />
        </Grid>
      </Grid>
      <Grid {...containerProps}>
        <GridHeader noCenter text='Residenza' />
        <Grid size={{ xs: 12, md: 6 }}>
          <InputField
            id='indirizzo-residenza'
            value={Arbitro?.indirizzoResidenza}
            onChange={i => aDispatch('indirizzoResidenza', i, 'upper')}
            label='Indirizzo'
            errorFunc={() => !Arbitro?.indirizzoResidenza}
            adornment='zone'
            mode={mode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <InputField
            id='citta-residenza'
            value={Arbitro?.cittaResidenza}
            onChange={i => aDispatch('cittaResidenza', i, 'upper')}
            label='Città'
            errorFunc={() => !Arbitro?.cittaResidenza}
            adornment='city'
            mode={mode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 2 }}>
          <InputField
            id='cap-residenza'
            value={Arbitro?.capResidenza}
            onChange={i => aDispatch('capResidenza', i)}
            label='CAP'
            errorFunc={() => !Arbitro?.capResidenza}
            mode={mode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          {linkToGoogleMaps(
            formatIndirizzo({
              presso: Arbitro?.pressoResidenza,
              indirizzo: Arbitro?.indirizzoResidenza,
              citta: Arbitro?.cittaResidenza,
            }),
          )}
        </Grid>
        <GridCheckbox
          label='Domicilio uguale a residenza'
          value={!!domicilioResidenza}
          onChange={(_, x) => setDomicilioResidenza(x)}
          md={12}
          justifyContent='flex-start'
        />
        {!domicilioResidenza && (
          <>
            <GridHeader noCenter text='Domicilio' />
            <Grid size={{ xs: 12, md: 6 }}>
              <InputField
                id='indirizzo-domicilio'
                value={Arbitro?.indirizzoDomicilio}
                onChange={i => aDispatch('indirizzoDomicilio', i, 'upper')}
                label='Indirizzo'
                errorFunc={() => !Arbitro?.indirizzoDomicilio}
                adornment='zone'
                mode={mode}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <InputField
                id='citta-domicilio'
                value={Arbitro?.cittaDomicilio}
                onChange={i => aDispatch('cittaDomicilio', i, 'upper')}
                label='Città'
                errorFunc={() => !Arbitro?.cittaDomicilio}
                adornment='city'
                mode={mode}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <InputField
                id='cap-domicilio'
                value={Arbitro?.capDomicilio}
                onChange={i => aDispatch('capDomicilio', i)}
                label='CAP'
                errorFunc={() => !Arbitro?.capDomicilio}
                mode={mode}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {linkToGoogleMaps(
                formatIndirizzo({
                  presso: Arbitro?.pressoDomicilio,
                  indirizzo: Arbitro?.indirizzoDomicilio,
                  citta: Arbitro?.cittaDomicilio,
                }),
              )}
            </Grid>
          </>
        )}
      </Grid>
      <Grid {...containerProps}>
        <GridHeader noCenter text='Contatti' />
        <Grid size={{ xs: 12, md: 6 }}>
          <InputField
            id='telefono'
            value={Arbitro?.telefono}
            onChange={i => aDispatch('telefono', i.replace(/\s/g, ''))}
            label='Telefono'
            errorFunc={() => !Arbitro?.telefono}
            type='tel'
            adornment='phone'
            mode={mode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <InputField
            id='mail'
            value={Arbitro?.mail}
            onChange={i => aDispatch('mail', i.replace(/\s/g, ''), 'lower')}
            label='E-mail'
            errorFunc={() => !Arbitro?.mail}
            type='email'
            adornment='email'
            mode={mode}
          />
        </Grid>
      </Grid>
      <Grid {...containerProps}>
        <GridHeader noCenter text='Coordinate bancarie' />
        <Grid size={{ xs: 12, md: 6 }}>
          <InputField
            id='codiceIBAN'
            value={Arbitro?.codiceIBAN}
            onChange={i =>
              aDispatch('codiceIBAN', i.replace(/\W/g, ''), 'upper')
            }
            label='Codice IBAN'
            errorFunc={() => !Arbitro?.codiceIBAN}
            mode={mode}
          />
        </Grid>
        <Grid size={{ xs: 12, md: admin || designatore ? 4 : 6 }}>
          <InputField
            id='nome-banca'
            value={Arbitro?.nomeBanca}
            onChange={i => aDispatch('nomeBanca', i, 'upper')}
            label='Nome banca'
            errorFunc={() => !Arbitro?.nomeBanca}
            mode={mode}
          />
        </Grid>
        {(admin || designatore) && (
          <Grid size={{ xs: 12, md: 2 }}>
            <InputField
              id='costo-km'
              value={Arbitro?.costoKm}
              onChange={i => aDispatch('costoKm', i)}
              label='Costo €/km'
              type='number'
              mode={mode}
            />
          </Grid>
        )}
      </Grid>
      <Grid {...containerProps}>
        <GridHeader noCenter>
          Disponibilità settimanali &nbsp;
          <IconButton
            style={{ color: colours.grey }}
            onClick={handleClickOpenDisponibilita}
          >
            <Tooltip title='Maggiori informazioni'>
              <InfoIcon />
            </Tooltip>
          </IconButton>
        </GridHeader>
        {Arbitro?.disponibilitaSettimanali?.length > 0 && (
          <Grid size={{ xs: 12, md: 9 }}>
            {Arbitro?.disponibilitaSettimanali?.map((x, index) => (
              <GridWeeklyTimeSlot
                key={index}
                index={index}
                x={x}
                values={Arbitro?.disponibilitaSettimanali}
                onChange={x => aDispatch('disponibilitaSettimanali', x)}
              />
            ))}
          </Grid>
        )}
        <GridWeeklyTimeSlotChoice
          values={Arbitro?.disponibilitaSettimanali}
          onChange={x => aDispatch('disponibilitaSettimanali', x)}
        />
      </Grid>
      <Grid {...containerProps}>
        <GridHeader noCenter>
          Indisponibilità puntuali &nbsp;
          <IconButton
            style={{ color: colours.grey }}
            onClick={handleClickOpenIndisp}
          >
            <Tooltip title='Maggiori informazioni'>
              <InfoIcon />
            </Tooltip>
          </IconButton>
        </GridHeader>
        <DatesPicker
          id='indisponibilita-puntuali'
          label='Indisponibilità puntuali'
          value={fIndPun('in')}
          onChange={setIndisponibilitaPuntuali}
          onInputChange={handleClickOpenIndispTyping}
          minDate={minDate}
          maxDate={maxDate}
          mode={mode}
          isMobile={isMobile}
        />
      </Grid>
      {(admin || designatore) && (
        <Grid {...containerProps}>
          <GridHeader noCenter>
            Società incompatibili &nbsp;
            <IconButton
              style={{ color: colours.grey }}
              onClick={handleClickOpenSocieta}
            >
              <Tooltip title='Maggiori informazioni'>
                <InfoIcon />
              </Tooltip>
            </IconButton>
          </GridHeader>
          <Grid size={12}>
            <AutoComplete
              id='codici-societa-incompatibili'
              value={affiliazioniLight?.filter(a =>
                Arbitro?.codiciSocietaIncompatibili?.includes(a.id),
              )}
              blurOnSelect={false}
              multiple
              optional
              options={affiliazioniLight}
              getOptionLabel={op => `${op.codiceSocieta} - ${op.nomeSocieta}`}
              noOptionsText='Nessuna società trovata'
              label='Società incompatibili'
              onChange={i =>
                aDispatch(
                  'codiciSocietaIncompatibili',
                  i.map(a => a.id),
                )
              }
              mode={mode}
            />
          </Grid>
        </Grid>
      )}
      {(admin || designatore) && (
        <Grid {...containerProps}>
          <GridHeader noCenter>
            Arbitro in attività &nbsp;
            <IconButton
              style={{ color: colours.grey }}
              onClick={handleClickOpenActive}
            >
              <Tooltip title='Maggiori informazioni'>
                <InfoIcon />
              </Tooltip>
            </IconButton>
          </GridHeader>
          <GridCheckbox
            label='Arbitro attivo'
            value={Arbitro?.isActive !== false}
            onChange={(_, x) => aDispatch('isActive', x)}
            md={12}
            justifyContent='flex-start'
          />
        </Grid>
      )}
      &nbsp;
      {(admin || designatore) && (
        <Grid size={12}>
          <InputField
            id='note'
            value={Arbitro?.note}
            onChange={i => aDispatch('note', i)}
            label='Note del designatore'
            mode={mode}
          />
        </Grid>
      )}
      <Grid {...containerProps}>
        <Grid size='grow'>
          <center>
            <Button error to='/arbitri' children='Indietro' />
          </center>
        </Grid>
        <Grid size='grow'>
          <center>
            <Button
              dark
              loading={isCreating || isUpdating}
              onClick={confirmArbitro}
            >
              Salva
            </Button>
          </center>
        </Grid>
      </Grid>
      <Dialog
        open={openDisponibilita}
        handleClose={handleCloseDisponibilita}
        title={titles.titleDisponibilita}
        textContent={phrases.phraseDisponibilita}
      />
      <Dialog
        open={openSocieta}
        handleClose={handleCloseSocieta}
        title={titles.titleSocieta}
        textContent={phrases.phraseSocieta}
      />
      <Dialog
        open={openIndisp}
        handleClose={handleCloseIndisp}
        title={titles.titleIndisp}
        textContent={phrases.phraseIndisp}
      />
      <Dialog
        open={openIndispTyping}
        handleClose={handleCloseIndispTyping}
        title={titles.titleIndispTyping}
        textContent={phrases.phraseIndispTyping}
      />
      <Dialog
        open={openActive}
        handleClose={handleCloseActive}
        title={titles.titleActive}
        textContent={phrases.phraseActive}
      />
    </Grid>
  )
}

export default Arbitro
